import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  Activity,
  DollarSign,
  Zap,
  Users,
  RefreshCw,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import toast from 'react-hot-toast';
import usageService from '../../../services/usageService';

const UsageDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState([]);
  const [users, setUsers] = useState([]);

  // Default: last 30 days
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, dailyRes, usersRes] = await Promise.all([
        usageService.getSummary(startDate, endDate),
        usageService.getDaily(startDate, endDate),
        usageService.getUsers(startDate, endDate),
      ]);
      setSummary(summaryRes.data);
      setDaily(dailyRes.data?.daily || []);
      setUsers(usersRes.data?.users || []);
    } catch (err) {
      console.error('Failed to fetch usage data:', err);
      toast.error('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = () => {
    fetchData();
  };

  const formatNumber = (n) => {
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return n?.toLocaleString() || '0';
  };

  const formatCost = (c) => `$${(c || 0).toFixed(4)}`;

  const truncateUserId = (id) => {
    if (!id) return 'unknown';
    if (id.length > 16) return id.substring(0, 8) + '...';
    return id;
  };

  return (
    <div className="min-h-screen bg-bg-100 text-text-100">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-bg-100/80 backdrop-blur-sm border-b border-border-200/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 rounded-lg hover:bg-bg-200 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold">Usage Dashboard</h1>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-1.5 rounded-lg bg-bg-200 border border-border-300/20 text-sm text-text-100"
              />
              <span className="text-text-400 text-sm">~</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-1.5 rounded-lg bg-bg-200 border border-border-300/20 text-sm text-text-100"
              />
              <button
                onClick={handleDateChange}
                className="px-4 py-1.5 rounded-lg bg-sedaily-500 text-white text-sm font-medium hover:bg-sedaily-600 transition-colors flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin text-text-400" />
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <SummaryCard
                icon={Activity}
                label="Total Requests"
                value={formatNumber(summary?.requestCount || 0)}
                color="text-blue-400"
              />
              <SummaryCard
                icon={Zap}
                label="Input Tokens"
                value={formatNumber(summary?.totalInputTokens || 0)}
                color="text-green-400"
              />
              <SummaryCard
                icon={Zap}
                label="Output Tokens"
                value={formatNumber(summary?.totalOutputTokens || 0)}
                color="text-purple-400"
              />
              <SummaryCard
                icon={DollarSign}
                label="Estimated Cost"
                value={formatCost(summary?.totalCost)}
                color="text-yellow-400"
              />
            </div>

            {/* Daily Trend Chart */}
            <div className="bg-bg-200/50 rounded-xl p-6" style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}>
              <h2 className="text-lg font-semibold mb-4">Daily Usage Trend</h2>
              {daily.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={daily}>
                    <defs>
                      <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis
                      dataKey="date"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickFormatter={(d) => d.substring(5)}
                    />
                    <YAxis
                      yAxisId="tokens"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickFormatter={(v) => formatNumber(v)}
                    />
                    <YAxis
                      yAxisId="cost"
                      orientation="right"
                      tick={{ fill: '#9ca3af', fontSize: 12 }}
                      tickFormatter={(v) => `$${v.toFixed(2)}`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#f3f4f6',
                      }}
                      formatter={(value, name) => {
                        if (name === 'estimatedCost') return [`$${value.toFixed(4)}`, 'Cost'];
                        return [formatNumber(value), name === 'inputTokens' ? 'Input' : name === 'outputTokens' ? 'Output' : name];
                      }}
                    />
                    <Area
                      yAxisId="tokens"
                      type="monotone"
                      dataKey="inputTokens"
                      stroke="#10b981"
                      fill="none"
                      strokeWidth={2}
                    />
                    <Area
                      yAxisId="tokens"
                      type="monotone"
                      dataKey="outputTokens"
                      stroke="#a855f7"
                      fill="none"
                      strokeWidth={2}
                    />
                    <Area
                      yAxisId="cost"
                      type="monotone"
                      dataKey="estimatedCost"
                      stroke="#3b82f6"
                      fill="url(#colorCost)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center py-16 text-text-400">
                  No data for selected period
                </div>
              )}
              <div className="flex items-center gap-6 mt-3 text-xs text-text-400 justify-center">
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-green-500 inline-block rounded" /> Input Tokens</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-purple-500 inline-block rounded" /> Output Tokens</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-blue-500 inline-block rounded" /> Cost</span>
              </div>
            </div>

            {/* Tool Breakdown */}
            {summary?.byTool?.length > 0 && (
              <div className="bg-bg-200/50 rounded-xl p-6" style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}>
                <h2 className="text-lg font-semibold mb-4">Usage by Tool</h2>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={summary.byTool} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                    <YAxis type="category" dataKey="tool" tick={{ fill: '#9ca3af', fontSize: 12 }} width={100} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        color: '#f3f4f6',
                      }}
                    />
                    <Bar dataKey="requestCount" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Requests" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* User Breakdown Table */}
            <div className="bg-bg-200/50 rounded-xl p-6" style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}>
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Usage by User
              </h2>
              {users.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-text-400 border-b border-border-300/10">
                        <th className="pb-3 pr-4">#</th>
                        <th className="pb-3 pr-4">Email</th>
                        <th className="pb-3 pr-4">Tier</th>
                        <th className="pb-3 pr-4 text-right">Requests</th>
                        <th className="pb-3 pr-4 text-right">Input Tokens</th>
                        <th className="pb-3 pr-4 text-right">Output Tokens</th>
                        <th className="pb-3 text-right">Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, idx) => (
                        <tr
                          key={user.userId}
                          className="border-b border-border-300/5 hover:bg-bg-300/30 transition-colors"
                        >
                          <td className="py-3 pr-4 text-text-400">{idx + 1}</td>
                          <td className="py-3 pr-4 text-sm">{user.email || truncateUserId(user.userId)}</td>
                          <td className="py-3 pr-4">
                            <TierBadge tier={user.tier} />
                          </td>
                          <td className="py-3 pr-4 text-right">{user.requestCount?.toLocaleString()}</td>
                          <td className="py-3 pr-4 text-right">{formatNumber(user.inputTokens)}</td>
                          <td className="py-3 pr-4 text-right">{formatNumber(user.outputTokens)}</td>
                          <td className="py-3 text-right font-medium text-yellow-400">
                            {formatCost(user.estimatedCost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex items-center justify-center py-12 text-text-400">
                  No usage data available
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const TIER_STYLES = {
  admin: 'bg-red-500/20 text-red-400',
  enterprise: 'bg-purple-500/20 text-purple-400',
  pro: 'bg-blue-500/20 text-blue-400',
  free: 'bg-gray-500/20 text-gray-400',
};

const TierBadge = ({ tier }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIER_STYLES[tier] || TIER_STYLES.free}`}>
    {tier || 'free'}
  </span>
);

const SummaryCard = ({ icon: Icon, label, value, color }) => (
  <div
    className="bg-bg-200/50 rounded-xl p-5"
    style={{ border: '1px solid hsl(var(--border-300) / 0.12)' }}
  >
    <div className="flex items-center gap-3 mb-2">
      <Icon className={`w-5 h-5 ${color}`} />
      <span className="text-sm text-text-400">{label}</span>
    </div>
    <p className="text-2xl font-bold">{value}</p>
  </div>
);

export default UsageDashboard;
